// A drawing, stored as strokes rather than as an image.
//
// This is the single most consequential storage decision in the app, so it's
// worth writing down why.
//
// A 400x400 PNG data URL is roughly 40-120KB. Twelve players times twelve
// pages is well over a hundred drawings in one game, all of which have to
// travel through Firebase RTDB to every player in the room, live. That is tens
// of megabytes per game, into a database billed by bandwidth, for something
// that is mostly white.
//
// The same drawing as strokes is a few kilobytes: a list of paths, each a list
// of points. It also gets three things for free that a bitmap doesn't:
//   - it scales, so a drawing made on a phone looks right on an iPad;
//   - it can be replayed, if we ever want to animate the reveal;
//   - it diffs and compresses well, being mostly small integers.
//
// Coordinates are stored in a 0..1000 square, NOT in pixels. The canvas is
// whatever size the device gives it, and normalising at capture time means a
// drawing never depends on the screen it was made on.

export const CANVAS_UNITS = 1000;

// Rounding to whole units costs nothing visible at any realistic canvas size
// and roughly halves the JSON, since it kills long decimal tails.
const round = (n) => Math.round(n);

export const clampUnit = (n) => Math.min(CANVAS_UNITS, Math.max(0, round(n)));

/**
 * Turn a pointer position into normalised units.
 */
export const toUnits = (clientX, clientY, rect) => {
  if (!rect || !rect.width || !rect.height) return [0, 0];
  return [
    clampUnit(((clientX - rect.left) / rect.width) * CANVAS_UNITS),
    clampUnit(((clientY - rect.top) / rect.height) * CANVAS_UNITS),
  ];
};

/**
 * Drops points that add nothing.
 *
 * A pointer event stream is far denser than a drawing needs - a slow careful
 * line can emit hundreds of points a second, most of them a pixel apart. This
 * keeps a point only when it has moved a meaningful distance from the last one
 * kept, which typically removes 70-90% of them with no visible change.
 *
 * The final point of a stroke is always kept by the caller, so a line never
 * stops short of where the finger lifted.
 */
export const MIN_POINT_DISTANCE = 6;

export const farEnough = ([ax, ay], [bx, by], min = MIN_POINT_DISTANCE) => (
  Math.abs(ax - bx) + Math.abs(ay - by) >= min
);

export const createStroke = (color, width) => ({ c: color, w: width, p: [] });

// The paper colour. An ERASER is nothing more than a stroke in this colour
// (bug report, 2026-09-11: "an eraser tool"): it replays, scales, syncs and
// renders for the bots exactly like any other stroke, with no new kind of
// thing on the wire. A flood fill treats it as paper too, which is what an
// eraser should mean.
export const PAPER = '#fdfcf7';

/**
 * A FILL is the other kind of mark (bug report, 2026-09-11: "a fill tool that
 * will fill in an area with a color"). It is stored as the REGION it covered,
 * not as a seed point to be re-flooded on every device: rasterising the same
 * strokes on two browsers gives slightly different anti-aliased edges, and a
 * one-pixel gap that leaks on one phone and holds on another would show two
 * players two different drawings. The region is a list of horizontal runs on a
 * FILL_GRID x FILL_GRID grid over the canvas, `[row, x0, length]` in cells,
 * computed once on the device that tapped (see fill.js).
 *
 * `p: []` keeps every existing consumer (isDrawing, isBlankPage, the point
 * guard) honest without special cases.
 */
export const FILL_GRID = 250;
export const FILL_CELL = CANVAS_UNITS / FILL_GRID;

export const createFill = (color, runs) => ({ c: color, w: 0, p: [], r: runs });

export const isFill = (stroke) => Array.isArray(stroke?.r);

export const runCount = (strokes) => (strokes || []).reduce((n, s) => n + (s.r?.length || 0), 0);

/**
 * A drawing is an array of strokes; a stroke is `{ c, w, p }` with short keys
 * because every one of them is repeated on the wire.
 */
export const isDrawing = (value) => Array.isArray(value)
  && value.every((s) => s && typeof s === 'object' && Array.isArray(s.p));

export const pointCount = (strokes) => (strokes || []).reduce((n, s) => n + s.p.length, 0);

// What the size guard actually measures: a fill's runs cost about what points
// do on the wire, so they count against the same ceiling.
export const markCount = (strokes) => pointCount(strokes) + runCount(strokes);

/**
 * Rough size on the wire, for the guard below.
 */
export const approximateBytes = (strokes) => JSON.stringify(strokes || []).length;

// A hard ceiling, because a single pathological drawing must not be able to
// wedge a whole game. Reached only by scribbling continuously for minutes;
// a normal drawing is a few hundred points.
export const MAX_POINTS = 4000;

export const isTooBig = (strokes) => markCount(strokes) > MAX_POINTS;

/**
 * Drops the oldest strokes until the drawing fits.
 *
 * Deliberately trims from the START: the most recent marks are the ones the
 * person is looking at, so losing the beginning of a long scribble is far less
 * alarming than watching the thing you just drew vanish.
 */
export const trimToFit = (strokes) => {
  const out = [...(strokes || [])];
  while (markCount(out) > MAX_POINTS && out.length > 1) out.shift();
  return out;
};

/**
 * The path string for one stroke, in canvas units.
 *
 * A single point is emitted as a dot (a zero-length line with a round cap),
 * because tapping the canvas should leave a mark - people dot eyes.
 */
export const strokePath = (stroke) => {
  const pts = stroke?.p || [];
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M${pts[0][0]} ${pts[0][1]} L${pts[0][0]} ${pts[0][1]}`;
  return pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x} ${y}`).join(' ');
};

/**
 * The path string for one fill: one rectangle per run, in canvas units. A
 * single <path> with fill and no stroke, so a region of a thousand runs is
 * still one DOM node.
 */
export const fillPath = (stroke) => {
  const runs = stroke?.r || [];
  if (runs.length === 0) return '';
  return runs.map(([row, x0, len]) => (
    `M${x0 * FILL_CELL} ${row * FILL_CELL}h${len * FILL_CELL}v${FILL_CELL}h${-len * FILL_CELL}z`
  )).join('');
};
